import { ReactNode, createContext, useContext, useState } from 'react';

import ToastNotification from './toastNotification';
interface ToastProps {
  open: boolean;
  severity: string;
  message: string;
}
interface AlertModalProps {
  open: boolean;
  headerText: string;
  alertText: string;
  callback: () => void;
}

interface ConfirmationModalProps {
  open: boolean;
  headerText: string;
  alertText: string;
  buttonText1: string;
  buttonText2: string;
  callback: () => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

interface ErrorToastProps {
  open: boolean;
  message: string;
  callback: () => void;
}

interface ToastNotificationContextProps {
  toast: ToastProps;
  createToastNotification: (message: string, severity: string) => void;
  removeToastHandler: () => void;
  alertModal: AlertModalProps;
  openAlertModal: (
    headerText: string,
    alertText: string,
    callback: () => void,
  ) => void;
  closeAlertModal: () => void;
  errorToast: ErrorToastProps;
  createErrorToast: (message: string, callback: () => void) => void;
  closeErrorToast: () => void;
  openSubmitErrorTicket: () => void;
  confirmationModal: ConfirmationModalProps;
  openConfirmationModal: (
    headerText: string,
    alertText: string,
    buttonText1: string,
    buttonText2: string,
    callback: () => void,
  ) => void;
  closeConfirmationModal: () => void;
  callConfirmationTrigger: () => void;
}

const AuthContext = createContext<ToastNotificationContextProps | undefined>(
  undefined,
);

const ToastNotificationContext = ({ children }: ToastProviderProps) => {
  const [toast, seToast] = useState<ToastProps>({
    open: false,
    severity: '',
    message: '',
  });
  const [alertModal, seAlertModal] = useState<AlertModalProps>({
    open: false,
    headerText: '',
    alertText: '',
    callback: () => {},
  });

  const [confirmationModal, seConfirmationModal] =
    useState<ConfirmationModalProps>({
      open: false,
      headerText: '',
      alertText: '',
      buttonText1: '',
      buttonText2: '',
      callback: () => {},
    });

  const [errorToast, setErrorToast] = useState<ErrorToastProps>({
    open: false,
    message: '',
    callback: () => {},
  });

  const createToastNotification = (message: string, severity: string) => {
    seToast({
      open: true,
      severity: severity,
      message: message,
    });
  };
  const removeToastHandler = () => {
    seToast({
      open: false,
      severity: '',
      message: '',
    });
  };

  const openAlertModal = (
    headerText: string,
    alertText: string,
    callback: () => void,
  ) => {
    seAlertModal({
      headerText,
      alertText,
      open: true,
      callback: callback,
    });
  };
  const closeAlertModal = () => {
    alertModal.callback();
    seAlertModal({
      headerText: '',
      alertText: '',
      open: false,
      callback: () => {},
    });
  };

  const openConfirmationModal = (
    headerText: string,
    alertText: string,
    buttonText1: string,
    buttonText2: string,
    callback: () => void,
  ) => {
    seConfirmationModal({
      headerText,
      alertText,
      open: true,
      buttonText1,
      buttonText2,
      callback: callback,
    });
  };

  const callConfirmationTrigger = () => {
    confirmationModal.callback();
    seConfirmationModal({
      headerText: '',
      alertText: '',
      buttonText1: '',
      buttonText2: '',
      open: false,
      callback: () => {},
    });
  };

  const closeConfirmationModal = () => {
    seConfirmationModal({
      headerText: '',
      alertText: '',
      buttonText1: '',
      buttonText2: '',
      open: false,
      callback: () => {},
    });
  };

  const createErrorToast = (message: string, callback: () => void) => {
    setErrorToast({
      open: true,
      message: message,
      callback: callback,
    });
  };
  const closeErrorToast = () => {
    setErrorToast({
      open: false,
      message: '',
      callback: () => {},
    });
  };

  const openSubmitErrorTicket = () => {
    errorToast.callback();
    setErrorToast({
      open: false,
      message: '',
      callback: () => {},
    });
  };

  const contextValue: ToastNotificationContextProps = {
    toast,
    createToastNotification,
    removeToastHandler,
    alertModal,
    openAlertModal,
    closeAlertModal,
    errorToast,
    createErrorToast,
    closeErrorToast,
    openSubmitErrorTicket,
    confirmationModal,
    openConfirmationModal,
    closeConfirmationModal,
    callConfirmationTrigger,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <ToastNotification />
    </AuthContext.Provider>
  );
};

const useToastNotificationContext = (): ToastNotificationContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Something went wrong.');
  }
  return context;
};

export { useToastNotificationContext, ToastNotificationContext };